// How long a leg takes, given when the driver leaves and which way they are
// heading.
//
// CommonJS so scripts/check-travel.js can require it with no transpiler, the
// same arrangement bookingRules.js uses. Pure -- no Firestore, no Vue, and no
// reading of the real clock: every function takes the time it should assume.
//
// ---------------------------------------------------------------------------
// Three things decide a leg
// ---------------------------------------------------------------------------
//
// 1. DISTANCE, via geo.roadDistanceKm, turned into free-flow minutes by a speed
//    that rises with the length of the leg -- a 40 km run uses expressway, a
//    2 km hop between two stops does not.
//
// 2. TIME OF DAY, as the multiplier bands the delivery team measured.
//
// 3. DIRECTION, which matters as much as the other two and is the reason this
//    module is more than a lookup table. See below.
//
// ---------------------------------------------------------------------------
// Why direction is modelled at all
// ---------------------------------------------------------------------------
//
// The corridors the delivery team named -- แจ้งวัฒนะ, งามวงศ์วาน, วิภาวดีรังสิต
// (แยกหลักสี่), รามคำแหง, ลาดพร้าว, ราชพฤกษ์ (แยกสาทร), กรุงธนบุรี, พระราม 9,
// เพชรบุรีตัดใหม่ (ประตูน้ำ) and พระราม 4 -- were every one of them qualified
// "ขาเข้าเมือง". That is not a coincidence: they are the northern and western
// radials between the depot in ไทรน้อย and the city, which is to say they
// are the depot's approach path.
//
// A direction-blind model charges the morning return leg the same 1.30 as the
// morning run in, when the outbound carriageway is actually flowing. Over five
// cars that is a large daily error in the wrong direction -- it makes the day
// look fuller than it is.
//
// So: during a rush band the multiplier applies only to the congested
// direction, and the congested direction flips at midday.
//
//   ก่อนเที่ยง   ขาเข้าเมือง ติด   ขาออก คล่อง
//   บ่าย-เย็น    ขาออกเมือง ติด   ขาเข้า คล่อง
//
// ASSUMPTION worth confirming with the delivery team: the team described these
// corridors inbound, which is the morning observation. This module assumes the
// same chokepoints jam outbound in the evening -- the ordinary Bangkok pattern,
// and the one that governs the return leg. Pin a corridor to a single direction
// with its `only` field if that turns out to be wrong for a particular road.

const geo = require('./geo')

// Multiplier by time of day, as measured by the delivery team. `from` is
// inclusive, `to` exclusive, both in minutes since midnight.
//
// The team's table starts at 08:00 and ends at "evening"; the quiet hours on
// either side are filled in at 1.00 so the integrator always finds a band.
// EVENING_END is where 15:00-onward rush is taken to stop -- see the return-leg
// deadline note in scripts/README.md; nothing should still be driving then.
const BANDS = [
  { from: 0, to: 480, factor: 1.00, label: 'ก่อนเร่งด่วนเช้า' },
  { from: 480, to: 570, factor: 1.30, label: 'เร่งด่วนเช้า' },        // 08:00-09:30
  { from: 570, to: 690, factor: 0.95, label: 'รถโล่ง' },              // 09:30-11:30
  { from: 690, to: 810, factor: 1.00, label: 'ปกติ' },                // 11:30-13:30
  { from: 810, to: 900, factor: 1.05, label: 'ติดเล็กน้อย' },          // 13:30-15:00
  { from: 900, to: 1140, factor: 1.30, label: 'เร่งด่วนเย็น' },        // 15:00-19:00
  { from: 1140, to: 1440, factor: 1.00, label: 'หลังเร่งด่วน' }
]

// A band counts as rush -- and so becomes direction-sensitive -- at or above
// this. Derived from the bands rather than hard-coded as a list of them, so
// retuning a band cannot leave this behind.
const RUSH_THRESHOLD = 1.30

// What the free-flowing carriageway costs during a rush band. Not 0.95: the
// city is busy in both directions at peak, it just is not jammed.
const FREE_DIRECTION_FACTOR = 1.00

// Before this, the jam is inbound; after it, outbound.
const DIRECTION_FLIP_MINUTE = 720 // 12:00

// The corridors the delivery team called out as worse than the ordinary rush --
// "some area may x2". Each is an approximate bounding box: the app has no road
// geometry, and a box around the corridor is the closest a pure module can get.
//
// THESE BOXES ARE ESTIMATES and are meant to be tuned by the people who drive
// them. Widening or narrowing one is a data edit, not a logic change.
//
// A leg is treated as using a corridor when EITHER endpoint falls inside the
// box -- a leg that starts or ends on แจ้งวัฒนะ at 08:30 sat in that traffic
// whichever end it happened at.
//
// `only`, if set to 'inbound' or 'outbound', pins the corridor to that
// direction. Left unset, it applies to whichever direction is congested at the
// time -- see the ASSUMPTION note at the top of this file.
const CORRIDORS = [
  { name: 'ถนนแจ้งวัฒนะ', factor: 2.0, lat: [13.870, 13.910], lng: [100.490, 100.630] },
  { name: 'ถนนงามวงศ์วาน', factor: 2.0, lat: [13.845, 13.875], lng: [100.490, 100.590] },
  { name: 'ถนนวิภาวดีรังสิต (แยกหลักสี่)', factor: 2.0, lat: [13.860, 13.920], lng: [100.550, 100.590] },
  { name: 'ถนนรามคำแหง', factor: 2.0, lat: [13.740, 13.790], lng: [100.600, 100.700] },
  { name: 'ถนนลาดพร้าว', factor: 2.0, lat: [13.790, 13.840], lng: [100.550, 100.650] },
  { name: 'ถนนราชพฤกษ์ (แยกสาทร)', factor: 2.0, lat: [13.690, 13.760], lng: [100.430, 100.500] },
  { name: 'ถนนกรุงธนบุรี', factor: 2.0, lat: [13.700, 13.740], lng: [100.470, 100.510] },
  { name: 'ถนนพระราม 9', factor: 2.0, lat: [13.730, 13.770], lng: [100.560, 100.620] },
  { name: 'ถนนเพชรบุรีตัดใหม่ (ประตูน้ำ)', factor: 2.0, lat: [13.740, 13.760], lng: [100.520, 100.580] },
  { name: 'ถนนพระราม 4', factor: 2.0, lat: [13.700, 13.745], lng: [100.510, 100.590] }
]

// Free-flow speed rises with the length of the leg, because a longer leg spends
// more of itself on วงแหวน / โทลล์เวย์ / มอเตอร์เวย์ and less on surface
// streets. A single average speed cannot describe both a 45 km depot run and a
// 1.5 km hop between two stops in the same ซอย.
//
// These are MARGINAL rates, not averages: the first 5 km of any leg run at
// 20 km/h, the next 10 at 28, and so on. That is how a real trip is shaped --
// slow surface streets at each end with fast road in the middle -- and it makes
// the total strictly increasing in distance, which a table of average speeds
// does NOT.
//
// The average-speed version of this table was a bug, and check-travel.js caught
// it: at a 15 km boundary, 15 km at 28 km/h is 32 minutes while 16 km at
// 40 km/h is 24, so driving one kilometre further arrived eight minutes sooner.
// A planner handed that will build detours to exploit it.
//
// CALIBRATION: these four rates are the least certain numbers in this module --
// informed estimates, not measurements. They are the first thing to tune
// against real GPS traces or Google `duration_in_traffic`. check-travel.js
// deliberately asserts no absolute minute value that depends on them.
const SPEEDS = [
  { upToKm: 5, kmh: 20 },
  { upToKm: 15, kmh: 28 },
  { upToKm: 30, kmh: 40 },
  { upToKm: Infinity, kmh: 55 }
]

// Between provinces -- an out-of-area route (dispatchPlan.planTrips) -- the
// road is highway from the first kilometre, and the city's ramp of 20/28/40
// km/h cost every leg an hour it does not spend. Calibrated on 2026-10-07's two
// routes against Google: A 305 km in 319 min, B 269 km in 243 min (57 and 66
// km/h). Five km at town speed to get out of where the customer is, then 65.
// The rush bands and corridors are Bangkok's and are not applied either: the
// leg is timed by distance alone.
const HIGHWAY_SPEEDS = [
  { upToKm: 5, kmh: 30 },
  { upToKm: Infinity, kmh: 65 }
]

function freeFlowMinutes (km, speeds) {
  if (!isFinite(km) || km <= 0) return 0
  const table = speeds || SPEEDS

  let remaining = km
  let from = 0
  let minutes = 0

  for (let i = 0; i < table.length && remaining > 0; i++) {
    const span = Math.min(remaining, table[i].upToKm - from)
    minutes += span / table[i].kmh * 60
    remaining -= span
    from = table[i].upToKm
  }

  return minutes
}

// The effective average speed over a leg of this length, derived from the
// marginal table rather than stated separately so the two cannot disagree.
// Reporting only -- nothing in the model divides by it.
function freeFlowSpeedKmh (km) {
  const minutes = freeFlowMinutes(km)
  if (!minutes) return SPEEDS[0].kmh
  return km / (minutes / 60)
}

// Minutes since midnight are the internal representation everywhere here: they
// make band arithmetic trivial and carry no timezone of their own. Callers hold
// the date; this module only knows the shape of a day.
function toMinutes (clock) {
  if (typeof clock === 'number') return clock
  if (!clock || typeof clock !== 'string') return null
  const m = clock.trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return null
  const h = Number(m[1])
  const min = Number(m[2])
  if (h > 23 || min > 59) return null
  return h * 60 + min
}

function toClock (minutes) {
  if (!isFinite(minutes)) return ''
  // A leg that runs past midnight would otherwise render as "25:10". Routes do
  // not, but the formatter should not be the thing that lies if one ever does.
  const m = Math.round(minutes)
  const day = Math.floor(m / 1440)
  const rest = m - day * 1440
  const h = Math.floor(rest / 60)
  const mm = rest % 60
  const pad = n => (n < 10 ? '0' : '') + n
  return `${pad(h)}:${pad(mm)}${day ? ` (+${day})` : ''}`
}

function bandAt (minutes) {
  const t = ((minutes % 1440) + 1440) % 1440
  for (let i = 0; i < BANDS.length; i++) {
    if (t >= BANDS[i].from && t < BANDS[i].to) return BANDS[i]
  }
  return BANDS[BANDS.length - 1]
}

function bandFactor (minutes) {
  return bandAt(minutes).factor
}

function isRush (minutes) {
  return bandAt(minutes).factor >= RUSH_THRESHOLD
}

// Which way the jam runs at this time of day.
function congestedDirection (minutes) {
  const t = ((minutes % 1440) + 1440) % 1440
  return t < DIRECTION_FLIP_MINUTE ? 'inbound' : 'outbound'
}

function inBox (point, box) {
  return geo.isPoint(point) &&
    point.lat >= box.lat[0] && point.lat <= box.lat[1] &&
    point.lng >= box.lng[0] && point.lng <= box.lng[1]
}

// Every corridor either endpoint touches. Returned as a list rather than a
// number so a plan can tell staff WHICH road it expects to be stuck on -- an
// ETA nobody can question is an ETA nobody can correct.
function corridorsFor (from, to) {
  return CORRIDORS.filter(c => inBox(from, c) || inBox(to, c))
}

// The multiplier in force at one instant, for one leg.
//
// Off-peak, the band applies flat: direction is irrelevant when nothing is
// jammed. In a rush band the leg is charged the band -- amplified by any
// corridor -- only if it runs into the jam; against it, traffic is ordinary.
//
// A 'neutral' leg (both endpoints within DIRECTION_THRESHOLD_KM of the same
// distance from the centre -- a hop between two nearby stops) takes the band
// unamplified. It is inside the congestion, not driving through the length of
// it, so neither the free-direction discount nor the corridor x2 fits.
function factorAt (minutes, direction, corridorFactor) {
  const band = bandAt(minutes)
  if (band.factor < RUSH_THRESHOLD) return band.factor
  if (direction === 'neutral') return band.factor
  if (direction !== congestedDirection(minutes)) return FREE_DIRECTION_FACTOR
  return Math.max(band.factor, corridorFactor || 1)
}

function corridorFactorFor (from, to, direction) {
  return corridorsFor(from, to).reduce((max, c) => {
    if (c.only && c.only !== direction) return max
    return Math.max(max, c.factor)
  }, 1)
}

// Guard for the integrator: a band factor of zero or less would divide by zero
// and never consume the leg.
const MIN_FACTOR = 0.05

// Consume the leg band by band instead of multiplying by the departure band.
//
// This is the whole point of the module. A 40-minute leg leaving at 09:20 is
// not 40 minutes of x1.30 -- ten of them are, and the rest run in the x0.95
// that starts at 09:30. Multiplying by the departure band produces a model in
// which leaving ten minutes LATER arrives fifteen minutes EARLIER, and a
// planner will happily exploit that. Staff notice immediately, and once they
// catch the tool being obviously wrong once, they stop trusting the ETAs that
// were right.
//
// Each pass consumes as much free-flow distance as the current band has clock
// time for, then moves to the next band.
function integrate (freeMinutes, departMinutes, factorFn) {
  let clock = departMinutes
  let remaining = freeMinutes
  let elapsed = 0
  let guard = 0

  while (remaining > 1e-9) {
    // Runaway guard: 200 passes is far more bands than a day holds, so hitting
    // it means a factor or band table that cannot terminate. Returning what has
    // accumulated beats hanging the browser.
    if (++guard > 200) break

    const factor = Math.max(MIN_FACTOR, factorFn(clock))
    const band = bandAt(clock)
    const t = ((clock % 1440) + 1440) % 1440

    // Clock time left in this band, and the free-flow minutes that buys.
    const clockLeft = band.to - t
    const canConsume = clockLeft / factor

    const consumed = Math.min(remaining, canConsume)
    const spent = consumed * factor

    elapsed += spent
    clock += spent
    remaining -= consumed

    // Nudge across the boundary when the band is exhausted to the millisecond,
    // so the next pass reads the next band rather than re-reading this one.
    if (remaining > 1e-9 && consumed >= canConsume - 1e-9) clock = Math.max(clock, departMinutes + elapsed + 1e-6)
  }

  return elapsed
}

// The public call. `from` and `to` are { lat, lng }; `departAt` is minutes since
// midnight or an "HH:MM" string.
//
// Returns null rather than 0 when either endpoint cannot be placed: a leg of
// unknown length is not a leg of no length, and a planner must be able to tell
// the difference.
function travelMinutes (from, to, departAt, options) {
  const opts = options || {}
  const depart = toMinutes(departAt)
  if (depart == null) return null

  const km = opts.km != null ? opts.km : geo.roadDistanceKm(from, to)
  if (km == null || !isFinite(km)) return null
  if (km <= 0) return 0

  // An out-of-area leg: highway speeds, no bands (see HIGHWAY_SPEEDS).
  if (opts.highway) return freeFlowMinutes(km, HIGHWAY_SPEEDS)

  const free = freeFlowMinutes(km)
  const direction = geo.legDirection(from, to)
  const corridorFactor = corridorFactorFor(from, to, direction)

  return integrate(free, depart, clock => factorAt(clock, direction, corridorFactor))
}

function arriveAt (from, to, departAt, options) {
  const depart = toMinutes(departAt)
  const minutes = travelMinutes(from, to, departAt, options)
  if (depart == null || minutes == null) return null
  return depart + minutes
}

// Everything the leg is made of, for a plan preview or for explaining an ETA
// staff disagree with. Cheap enough to call while rendering a table.
function describeLeg (from, to, departAt, options) {
  const depart = toMinutes(departAt)
  const km = (options && options.km != null) ? options.km : geo.roadDistanceKm(from, to)
  const minutes = travelMinutes(from, to, departAt, options)
  if (depart == null || minutes == null) return null

  const direction = geo.legDirection(from, to)
  const corridors = corridorsFor(from, to).filter(c => !c.only || c.only === direction)

  return {
    km: km,
    freeFlowMinutes: freeFlowMinutes(km),
    minutes: minutes,
    depart: toClock(depart),
    arrive: toClock(depart + minutes),
    direction: direction,
    departBand: bandAt(depart).label,
    congestedDirection: congestedDirection(depart),
    againstTheJam: direction !== 'neutral' &&
      isRush(depart) && direction !== congestedDirection(depart),
    corridors: corridors.map(c => c.name),
    // What a flat departure-band model would have said. Kept because the gap is
    // the argument for this module existing, and it is the first thing to look
    // at when an ETA looks wrong.
    naiveMinutes: freeFlowMinutes(km) * bandFactor(depart)
  }
}

module.exports = {
  BANDS,
  CORRIDORS,
  SPEEDS,
  RUSH_THRESHOLD,
  FREE_DIRECTION_FACTOR,
  DIRECTION_FLIP_MINUTE,
  toMinutes,
  toClock,
  bandAt,
  bandFactor,
  isRush,
  congestedDirection,
  freeFlowSpeedKmh,
  freeFlowMinutes,
  HIGHWAY_SPEEDS,
  corridorsFor,
  corridorFactorFor,
  factorAt,
  travelMinutes,
  arriveAt,
  describeLeg
}
